import React from 'react';
import './MarketplacePage.scss';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Title from '../../shared/components/title/Title';
import MarketplaceTile from './components/marketplace-tile/MarketplaceTile';
import usePaddle from '../../shared/hooks/pricing/UsePaddle';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { showNewMessage } from '@intercom/messenger-js-sdk';
import {
  REACT_APP_INTERCOM_ENABLED,
  REACT_APP_INTERCOM_MESSENGER_ENABLED,
} from '../../shared/utils/config';
import { PROJECTS } from '../../shared/routes/path';
import { Addons } from '../../shared/types/account/Addons';
import { getMonitorTypeIcon } from '../prompt/preview/components/stats/helpers/StatsHelper';
import { MarketplacePurchase, openMarketplaceCheckout } from './services/MarketplaceService';
import { missingModels } from './helpers/MarketplaceModels';

/** Brand colour of each model logo (`getMonitorTypeIcon`), for the hover sparks; Grok's black flips in dark mode. */
const MODEL_ACCENTS: Record<string, string> = {
  'ico--gpt-bck': '#10a37f',
  'ico--claude-bck': '#d97757',
  'ico--gemini-bck': '#4285f4',
  'ico--deepseek-bck': '#4d6bfe',
  'ico--perplexity-bck': '#20808d',
  'ico--copilot-bck': '#0078d4',
  'ico--grok-bck': 'var(--grey-ux-800)',
  'ico--ai-overview-bck': '#4285f4',
  'ico--ai-mode': '#4285f4',
};

interface MarketplaceOffer {
  key: string;
  /** `ico--*` class of the badge by the title. */
  icon: string;
  /** CSS colour the tile's icon and sparks take on hover. */
  accent: string;
  title: string;
  description: string;
  buttonLabel: string;
  features: string[];
  purchase: MarketplacePurchase;
  /**
   * Paddle price. None is set yet: the billing webhook only understands plan subscriptions (it looks for the
   * `custom_data.type === 'product'` item), so a standalone purchase would charge the customer without granting
   * anything. Until the price AND that handling exist, the button is "Contact us" (Intercom).
   */
  priceId: string | null;
}

const MarketplacePage: React.FC = () => {
  const { t } = useTranslation();
  const { paddle } = usePaddle();
  const { accountResponse } = useAccount();

  if (!accountResponse?.addons.includes('MARKETPLACE')) {
    return <Navigate to={PROJECTS} replace />;
  }

  const has = (addon: Addons) => accountResponse.addons.includes(addon);
  // Same conditions on which `IntercomChat` boots the Messenger (guests get no Intercom).
  const hasChat =
    REACT_APP_INTERCOM_ENABLED &&
    REACT_APP_INTERCOM_MESSENGER_ENABLED &&
    accountResponse.role !== 'GUEST';

  const models: MarketplaceOffer[] = missingModels(accountResponse.features).map((model) => {
    const icon = getMonitorTypeIcon(model.monitorTypes[0]) ?? 'ico--chat';
    return {
      key: model.name,
      icon,
      accent: MODEL_ACCENTS[icon] ?? 'var(--primary-ux-500)',
      title: model.name,
      description: t('marketplaceModelDescription', { model: model.name }),
      buttonLabel: t('marketplaceAddModel'),
      features: [t('marketplaceModelAnswers'), t('marketplaceModelStats'), t('marketplaceModelSources')],
      purchase: { type: 'model', monitorTypes: model.monitorTypes },
      priceId: null,
    };
  });

  // An addon offer is dropped once the account has the addon it grants; additional prompts are a quantity
  // purchase, not an addon, so they are always on offer.
  const addons = (
    [
      !has('AGENCY') && {
        key: 'AGENCY',
        icon: 'ico--chart',
        accent: 'var(--primary-ux-500)',
        title: t('marketplaceAgencyTitle'),
        description: t('marketplaceAgencyDescription'),
        buttonLabel: t('marketplaceEnable'),
        features: [
          t('marketplaceAgencyGroups'),
          t('marketplaceAgencyTrends'),
          t('marketplaceAgencyInsights'),
        ],
        purchase: { type: 'addon', addonType: 'AGENCY' },
        priceId: null,
      },
      {
        key: 'ADDITIONAL_PROMPTS',
        icon: 'ico--plus',
        accent: 'var(--success-ux-500)',
        title: t('marketplaceAdditionalPromptsTitle'),
        description: t('marketplaceAdditionalPromptsDescription'),
        buttonLabel: t('marketplacePurchase'),
        features: [
          t('marketplacePromptsCoverage'),
          t('marketplacePromptsSlots'),
          t('marketplacePromptsScaling'),
        ],
        purchase: { type: 'addon', addonType: 'ADDITIONAL_PROMPTS' },
        priceId: null,
      },
      !has('EXPORT_DATA') && {
        key: 'EXPORT',
        icon: 'ico--download',
        accent: 'var(--warning-ux-500)',
        title: t('marketplaceExportTitle'),
        description: t('marketplaceExportDescription'),
        buttonLabel: t('marketplacePurchase'),
        features: [
          t('marketplaceExportCsv'),
          t('marketplaceExportExcel'),
          t('marketplaceExportPdf'),
          t('marketplaceExportScheduled'),
        ],
        purchase: { type: 'addon', addonType: 'EXPORT' },
        priceId: null,
      },
      !has('CLAUDE_WEB') && {
        key: 'CLAUDE_WEB_SEARCH',
        icon: 'ico--globe',
        accent: '#d97757',
        title: t('marketplaceClaudeWebSearchTitle'),
        description: t('marketplaceClaudeWebSearchDescription'),
        buttonLabel: t('marketplaceEnable'),
        features: [
          t('marketplaceClaudeRealTime'),
          t('marketplaceClaudeUpToDate'),
          t('marketplaceClaudePowered'),
        ],
        purchase: { type: 'addon', addonType: 'CLAUDE_WEB_SEARCH' },
        priceId: null,
      },
      !has('API_KEYS') && {
        key: 'API',
        icon: 'ico--zap',
        accent: 'var(--blue-100)',
        title: t('marketplaceApiAccessTitle'),
        description: t('marketplaceApiAccessDescription'),
        buttonLabel: t('marketplacePurchase'),
        features: [
          t('marketplaceApiRest'),
          t('marketplaceApiFullData'),
          t('marketplaceApiIntegration'),
          t('marketplaceApiKeys'),
        ],
        purchase: { type: 'addon', addonType: 'API' },
        priceId: null,
      },
    ] as (MarketplaceOffer | false)[]
  ).filter((offer): offer is MarketplaceOffer => !!offer);

  const renderTiles = (offers: MarketplaceOffer[]) => (
    <div className="marketplace-page__tiles">
      {offers.map((offer) => (
        <MarketplaceTile
          key={offer.key}
          icon={offer.icon}
          accent={offer.accent}
          title={offer.title}
          description={offer.description}
          features={offer.features}
          includedLabel={t('integrationsIncluded')}
          buttonLabel={offer.priceId ? offer.buttonLabel : t('marketplaceContactUs')}
          buttonIcon={offer.priceId ? 'ico--shopping-cart' : 'ico--chat'}
          // No price yet → sales through Intercom, with the offer pre-filled; needs the same Messenger the
          // help icon uses (IntercomChat), otherwise there is nothing to open.
          disabled={offer.priceId ? !paddle : !hasChat}
          onAction={() =>
            offer.priceId
              ? paddle && openMarketplaceCheckout(paddle, offer.priceId, accountResponse, offer.purchase)
              : showNewMessage(t('marketplaceContactMessage', { offer: offer.title }))
          }
        />
      ))}
    </div>
  );

  return (
    <div className="marketplace-page page-content">
      <div className="page-box">
        <Title title={t('marketplace')} />
        <div className="page-box__stats marketplace-page__content">
          {models.length > 0 && (
            <section className="marketplace-page__section">
              <div className="page-box__title">{t('marketplaceModels')}</div>
              {renderTiles(models)}
            </section>
          )}
          <section className="marketplace-page__section">
            <div className="page-box__title">{t('marketplaceAddons')}</div>
            {renderTiles(addons)}
          </section>
        </div>
      </div>
    </div>
  );
};

export default MarketplacePage;
